import { ArrowLeft, Plus } from "lucide-react";
import React from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Button, Card, CardContent, PermissionGuard } from "@/components";
import { useScopedPartnerNav } from "@/context/auth/scoped-partner-nav";
import { useI18n } from "@/context/i18n";

import { useCampaigns } from "./campaigns.hooks";
import { CampaignForm, CampaignsTable } from "./components";

export const CampaignsPage: React.FC = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { clientId } = useParams<{ clientId: string }>();
  const { partnerHomePath } = useScopedPartnerNav(clientId);
  const {
    campaigns,
    projects,
    plans,
    isCampaignFormOpen,
    isCampaignsLoading,
    isProjectsLoading,
    isPlansLoading,
    openCampaignForm,
    closeCampaignForm,
    handleCampaignFormSubmit,
    campaignsForm,
    editingId,
  } = useCampaigns(clientId ?? undefined);

  const handleBack = () => {
    if (clientId) {
      navigate(partnerHomePath);
    } else {
      closeCampaignForm();
    }
  };

  const handleViewDetails = React.useCallback(
    (campaignId: string) => {
      if (!clientId) {
        return;
      }

      navigate(`/clients/${clientId}/campaigns/${campaignId}/details`);
    },
    [clientId, navigate]
  );

  const campaignSummary = React.useMemo(() => {
    return {
      total: campaigns.length,
      active: campaigns.filter((campaign) => campaign.status === "active").length,
      draft: campaigns.filter((campaign) => campaign.status === "draft").length,
      paused: campaigns.filter((campaign) => campaign.status === "paused").length,
    };
  }, [campaigns]);

  return (
    <div className="flex flex-col w-full h-full space-y-6 p-4">
      <div className="flex flex-row items-start justify-between">
        <div className="flex flex-col w-full px-2">
          <div className="flex items-center gap-3 mb-3">
            {(isCampaignFormOpen || clientId) && (
              <Button
                type="button"
                variant="ghost"
                onClick={isCampaignFormOpen ? closeCampaignForm : handleBack}
                size="sm"
                className="p-3 hover:bg-muted/50 transition-colors"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
            )}
            <h1 className="text-3xl font-bold tracking-tight text-foreground">
              {t("campaigns.title")}
            </h1>
          </div>

          <p className="text-sm text-muted-foreground leading-relaxed">
            {t("campaigns.description")}
          </p>
        </div>

        {!isCampaignFormOpen && (
          <PermissionGuard permission="campaign:create">
            <div className="flex justify-end">
              <Button
                onClick={openCampaignForm}
                className="shadow-sm hover:shadow-md transition-shadow"
              >
                <Plus className="mr-2 h-4 w-4" />
                {t("generic.new")}
              </Button>
            </div>
          </PermissionGuard>
        )}
      </div>

      <CampaignForm
        open={isCampaignFormOpen}
        form={campaignsForm}
        onSubmit={handleCampaignFormSubmit}
        onCancel={closeCampaignForm}
        isEditing={Boolean(editingId)}
        projects={projects}
        plans={plans}
        isProjectsLoading={isProjectsLoading}
        isPlansLoading={isPlansLoading}
      />

      {!isCampaignFormOpen && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">
                  {t("campaigns.dashboard.total")}
                </p>
                <p className="text-2xl font-bold">{campaignSummary.total}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">
                  {t("campaigns.dashboard.active")}
                </p>
                <p className="text-2xl font-bold">{campaignSummary.active}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">
                  {t("campaigns.dashboard.draft")}
                </p>
                <p className="text-2xl font-bold">{campaignSummary.draft}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">
                  {t("campaigns.dashboard.paused")}
                </p>
                <p className="text-2xl font-bold">{campaignSummary.paused}</p>
              </CardContent>
            </Card>
          </div>
          <CampaignsTable
            campaigns={campaigns}
            projects={projects}
            isLoading={isCampaignsLoading}
            onViewDetails={handleViewDetails}
          />
        </>
      )}
    </div>
  );
};
